import { DateTime } from 'luxon';
import { TransportModeHelper } from '../../../helpers/transport-mode.helper';
import { ILine } from '../../../models/line.model';
import { Icon } from '../Icon';
import LineBadge from '../LineBadge';
import { LineHelper } from '../../../helpers/line.helper';
import { TranslationService } from '../../../services/translation.service';

/**
 * LineDescription
 * Displays a compact description of a line with optional transport mode icon, line badge, direction name,
 * and (optionally) the future "applies from" date.
 *
 * Behavior
 * - When showTransportMode is true, renders an Icon for the line's transport mode via TransportModeHelper.
 * - Always renders a LineBadge for the provided line.
 * - When showDirectionName is true, renders the line's directionName if present.
 * - When showDirectionName is true and a replacement-traffic suffix has been extracted, that suffix text is rendered; if a directionName is present, the suffix is rendered before the direction name to indicate replacement traffic.
 * - When showAppliesFromDateOnFutureLines is true and the line has an appliesFrom date in the future,
 *   renders a localized "from" text computed by LineHelper.getAppliesFromText and TranslationService.
 *
 * Props
 * - line: The line to render.
 * - showTransportMode: If true, show the vehicle icon before the badge.
 * - showDirectionName: If true, show the direction name next to the badge.
 * - showAppliesFromDateOnFutureLines?: If true, and appliesFrom > now, show the future start date.
 * - className?: Optional additional classes for the wrapper div.
 *
 * Remarks
 * - This component replaces the use of LineListShowcase with only one line.
 *
 * Example
 * <LineDescription
 *   line={line}
 *   showTransportMode
 *   showDirectionName
 *   showAppliesFromDateOnFutureLines
 *   className="mb-2"
 * />
 */
export default function LineDescription({
  line,
  showTransportMode,
  showDirectionName,
  showAppliesFromDateOnFutureLines = false,
  useShortName = false,
  className = '',
}: {
  line: ILine;
  showTransportMode: boolean;
  showDirectionName: boolean;
  showAppliesFromDateOnFutureLines?: boolean;
  useShortName?: boolean;
  className?: string;
}): React.ReactNode {
  if (!line) return null;

  const now = DateTime.now();
  const extractedReplacementTrafficSuffix = LineHelper.extractLineNameReplacementTrafficSuffix(line);

  return (
    <div className={`line-description d-flex flex-row gap-1 align-items-center ${className ?? ''}`.trimEnd()}>
      {showTransportMode && <Icon {...TransportModeHelper.getIcon(line.transportMode)} />}
      <LineBadge line={line} useShortName={useShortName} />
      {showDirectionName && (extractedReplacementTrafficSuffix || line.directionName) && (
        <span className="text-start">
          {extractedReplacementTrafficSuffix}
          {extractedReplacementTrafficSuffix && line.directionName && ': '}
          {line.directionName}
        </span>
      )}
      {showAppliesFromDateOnFutureLines && line.appliesFrom && line.appliesFrom > now && <span>({LineHelper.getAppliesFromText(line, TranslationService.get('common.from'))})</span>}
    </div>
  );
}
